import { createContext, useContext } from "react";
import type {
  ApprovalCallbackPayload,
  BreadcrumbEntry,
  CreateFolderCallbackPayload,
  DeleteCallbackPayload,
  FileManagerItem,
  FileManagerOperationCallbacks,
  FileManagerPermissions,
  LockedOperation,
  MoveCallbackPayload,
  RenameCallbackPayload,
  UpdateNoteCallbackPayload,
  UpdateTagsCallbackPayload,
  UploadFileItem,
} from "@/components/core/FileManager/FileManager.types.ts";

export interface FileManagerContextProps {
  currentFolderId: string | null;
  breadcrumb: BreadcrumbEntry[];
  openFolder: (id: string | null, name?: string, path?: BreadcrumbEntry[]) => void;
  goBack: () => void;
  navigateToBreadcrumb: (index: number) => void;

  search: string | null;
  searchInputValue: string;
  onSearch: (term: string) => void;
  onClearSearch: () => void;

  items: FileManagerItem[];
  folders: FileManagerItem[];
  files: FileManagerItem[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;

  upload: (
    files: UploadFileItem[],
    targetFolderId?: string,
    cbs?: FileManagerOperationCallbacks,
  ) => void;
  deleteFiles: (payload: DeleteCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  rename: (payload: RenameCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  createFolder: (payload: CreateFolderCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  moveItems: (payload: MoveCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  copyItems: (payload: MoveCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  updateTags: (payload: UpdateTagsCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  updateNote: (payload: UpdateNoteCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  approve: (payload: ApprovalCallbackPayload, cbs?: FileManagerOperationCallbacks) => void;
  getShareLink: (item: FileManagerItem) => Promise<string>;
  canGetShareLink: boolean;
  resolveDownloadUrl: (item: FileManagerItem) => Promise<string | null>;
  canDownload: (item: FileManagerItem) => boolean;

  isUploading: boolean;
  isDeleting: boolean;
  isRenaming: boolean;
  isCreatingFolder: boolean;
  isMoving: boolean;
  isCopying: boolean;
  isUpdatingTags: boolean;
  isUpdatingNote: boolean;
  isApprovingItem: boolean;
  isFetchingShareLink: boolean;

  mutationVersion: number;
  permissions: FileManagerPermissions;
  isLocked: (item: FileManagerItem | string | null, operation: LockedOperation) => boolean;
}

export const FileManagerContext = createContext<FileManagerContextProps>(
  {} as FileManagerContextProps,
);

export const useFileManager = () => {
  const ctx = useContext(FileManagerContext);
  if (!ctx) {
    throw new Error("useFileManager must be used within FileManagerContainer");
  }
  return ctx;
};
